import React, { useState, useRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import '../../App.css';

const AUTO_ADVANCE_DELAY_MS = 800;

const TutorialOverlay = ({ isOpen, onClose }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  // Swiperのインスタンスを保持するためのref
  const swiperRef = useRef(null);
  // 各スライドの動画要素を保持
  const videoRefs = useRef({});
  // 自動遷移タイマー
  const autoAdvanceTimer = useRef(null);

  // チュートリアルのスライド内容（全5ページ）
  const slides = [
    {
      id: 1,
      category: 'エレベータを呼ぶ',
      title: 'エレベータホールへ移動',
      description: '',
      videoUrl: `${process.env.PUBLIC_URL}/assets/tutorial_1.mp4`,
    },
    {
      id: 2,
      category: 'エレベータを呼ぶ',
      title: '階数を選択して呼ぶ',
      description: '',
      videoUrl: `${process.env.PUBLIC_URL}/assets/tutorial_2.mp4`,
    },
    {
      id: 3,
      category: 'エレベータに乗る',
      title: 'エレベータに乗る',
      description: '乗場ボタンの点灯を確認し、到着したエレベータにお乗りください。',
      videoUrl: `${process.env.PUBLIC_URL}/assets/tutorial_3.mp4`,
    },
    {
      id: 4,
      category: '次回以降',
      title: '階数を入れ替えて呼ぶ',
      description: 'マイエレベータに登録済みの場合、前回呼び出した階が自動で表示されます。',
      videoUrl: `${process.env.PUBLIC_URL}/assets/tutorial_4.mp4`,
    },
    {
      id: 5,
      category: 'お困りの時は',
      title: '使い方などの確認',
      description: '基本的な使い方やよくあるお問い合わせなどを確認できます。',
      videoUrl: `${process.env.PUBLIC_URL}/assets/tutorial_5.mp4`,
    },
    {
      id: 6,
      category: '',
      title: '',
      description: '',
      videoUrl: `${process.env.PUBLIC_URL}/assets/tutorial_6.mp4`,
    }
  ];

  // 動画終了時: 最終ページ以外は少し間をおいて次スライドへ
  const handleVideoEnded = useCallback((slideIndex) => {
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    // 最終ページでは自動遷移しない
    if (slideIndex >= slides.length - 1) return;
    autoAdvanceTimer.current = setTimeout(() => {
      swiperRef.current?.slideNext();
    }, AUTO_ADVANCE_DELAY_MS);
  }, [slides.length]);

  // スライド変更時: 遷移先の動画を先頭から再生、他は停止
  const handleSlideChange = useCallback((swiper) => {
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    Object.entries(videoRefs.current).forEach(([idx, video]) => {
      if (!video) return;
      if (Number(idx) === swiper.activeIndex) {
        video.currentTime = 0;
        video.play().catch(() => { });
      } else {
        video.pause();
      }
    });
  }, []);

  const handleComplete = () => {
    onClose(dontShowAgain);
  };

  if (!isOpen) return null;

  return (
    // 画面全体を覆うオーバーレイ（Header領域なしで画面100%を使用）
    <div
      className="fixed inset-0 z-50 bg-white flex flex-col h-[100dvh] w-full overflow-hidden"
      onTouchStart={() => { if (document.activeElement) document.activeElement.blur(); }}
      style={{ caretColor: 'transparent' }}
    >

      {/* メインコンテンツ領域 (Swiper) */}
      <div className="flex-1 w-full relative h-full border-t border-gray-200">
        <Swiper
          onSwiper={(swiper) => { swiperRef.current = swiper; }}
          onSlideChange={handleSlideChange}
          modules={[Pagination]}
          pagination={{ clickable: true }}
          className="w-full h-full tutorial-swiper"
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={slide.id}>
              <div className="w-full h-full flex flex-col items-center justify-center px-[calc(100%*2/12)] pb-[40px] select-none caret-transparent">

                {/* 1. カテゴリ (上部ラベル) */}
                <div className="shrink-0 w-full">
                  <p className="text-sm text-gray-500 mb-1 text-start">{slide.category || '\u00A0'}</p>
                </div>

                {/* 2. 動画エリア (3:5 アスペクト比、画面に応じて縮小) */}
                <div
                  className="shrink-0 overflow-hidden my-[1.5dvh] relative"
                  style={{ width: 'min(100%, calc(55dvh * 3 / 5))', aspectRatio: '3/5' }}
                >
                  {slide.videoUrl ? (
                    <>
                      <video
                        ref={(el) => { videoRefs.current[index] = el; }}
                        src={slide.videoUrl}
                        autoPlay={index === 0}
                        muted
                        playsInline
                        tabIndex={-1}
                        onEnded={() => handleVideoEnded(index)}
                        className="w-full h-full object-cover tutorial-video pointer-events-none"
                      />
                      {/* LINE WebView でのキャレット表示を防止するオーバーレイ */}
                      <div
                        className="absolute inset-0 z-10"
                        onMouseDown={(e) => e.preventDefault()}
                        onTouchEnd={() => {
                          if (document.activeElement && document.activeElement !== document.body) {
                            document.activeElement.blur();
                          }
                        }}
                      />
                    </>
                  ) : (
                    <div className="w-full h-full bg-gray-100 text-gray-400 text-sm text-center font-bold flex items-center justify-center">
                      <span>動画</span>
                    </div>
                  )}
                </div>

                {/* 3. タイトル・説明 / 最終ページはボタン表示 */}
                <div className="shrink-0 w-full">
                  {slide.id === slides.length ? (
                    /* 最終ページ: ボタン+チェックボックス */
                    <div className="w-full flex flex-col items-center my-2">
                      <button
                        onClick={handleComplete}
                        className="w-full py-3 rounded-xl font-bold text-white ux-bg-red shadow-md transition-transform active:scale-95 text-[17px]"
                      >
                        はじめる
                      </button>
                      <label className="flex items-center space-x-2 mt-4 cursor-pointer" onClick={() => setDontShowAgain(!dontShowAgain)}>
                        <span
                          className="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors cursor-pointer"
                          style={{
                            borderColor: dontShowAgain ? '#3ECE44' : '#D1D5DB',
                            backgroundColor: dontShowAgain ? '#3ECE44' : 'transparent',
                          }}
                        >
                          {dontShowAgain && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </span>
                        <span className="text-sm text-gray-700 font-medium">次回から表示しない</span>
                      </label>
                    </div>
                  ) : (
                    /* 通常ページ: タイトルと説明文 */
                    <>
                      {slide.title && (
                        <h3 className="text-lg font-bold text-gray-800 my-2 text-start">{slide.title}</h3>
                      )}
                      {slide.description && (
                        <p className="border-t-2 text-sm pt-2 leading-tight text-start whitespace-pre-line ux-text-asterisk">
                          {slide.description}
                        </p>
                      )}
                    </>
                  )}
                </div>

              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default TutorialOverlay;